import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Stethoscope, Check, X, Ban, RotateCcw, Eye } from "lucide-react";

export default function Therapists() {
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/therapists").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/therapists/${id}/status`, { status });
    toast.success("Status atualizado");
    load();
    setSelected(null);
  };

  return (
    <div>
      <PageHeader title="Hipnoterapeutas" subtitle="Aprove, suspenda ou reative profissionais" />
      {!list ? <div className="h-40 animate-pulse bg-secondary rounded-2xl" /> :
        list.length === 0 ? <EmptyState icon={Stethoscope} title="Nenhum profissional cadastrado" /> : (
        <div className="space-y-3">
          {list.map((t) => (
            <Card key={t.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`therapist-row-${t.id}`}>
              <Avatar className="w-11 h-11"><AvatarFallback className="bg-primary/10 text-primary">{t.name[0]}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{t.name}</span>
                  <StatusBadge status={t.status} />
                </div>
                <p className="text-sm text-muted-foreground truncate">{t.email} · {t.city || "—"} · {t.client_count} clientes</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" onClick={() => setSelected(t)} data-testid={`view-therapist-${t.id}`}><Eye className="w-4 h-4" /></Button>
                  </DialogTrigger>
                  {selected?.id === t.id && (
                    <DialogContent>
                      <DialogHeader><DialogTitle>{t.name}</DialogTitle></DialogHeader>
                      <div className="space-y-2 text-sm">
                        <Row k="E-mail" v={t.email} />
                        <Row k="Telefone" v={t.phone || "—"} />
                        <Row k="Cidade" v={t.city || "—"} />
                        <Row k="Perfil" v={t.profile_bio || "—"} />
                        <Row k="Pagamento" v={t.payment_status || "—"} />
                        <Row k="Cadastro" v={new Date(t.created_at).toLocaleDateString("pt-BR")} />
                        <Row k="Status" v={<StatusBadge status={t.status} />} />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" className="flex-1" onClick={() => setStatus(t.id, "active")} data-testid={`approve-${t.id}`}><Check className="w-4 h-4 mr-1" />Aprovar</Button>
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setStatus(t.id, "suspended")}><Ban className="w-4 h-4 mr-1" />Suspender</Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => setStatus(t.id, "rejected")}><X className="w-4 h-4" /></Button>
                      </div>
                    </DialogContent>
                  )}
                </Dialog>
                {t.status !== "active" && <Button size="sm" onClick={() => setStatus(t.id, "active")} data-testid={`quick-approve-${t.id}`}><Check className="w-4 h-4 mr-1" />Aprovar</Button>}
                {t.status === "active" && <Button size="sm" variant="outline" onClick={() => setStatus(t.id, "suspended")}><Ban className="w-4 h-4 mr-1" />Suspender</Button>}
                {t.status === "suspended" && <Button size="sm" variant="outline" onClick={() => setStatus(t.id, "active")}><RotateCcw className="w-4 h-4 mr-1" />Reativar</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between gap-4 border-b border-border pb-1.5"><span className="text-muted-foreground">{k}</span><span className="text-right font-medium">{v}</span></div>;
}
